import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import countriesCitiesData from '../lib/countries-cities.json';
import { slugify } from '../lib/slug';
import SEO from '../components/SEO';

export default function Countries() {
  const { t } = useTranslation();
  const countries = useMemo(() => {
    const arr = Array.isArray(countriesCitiesData?.countries) ? countriesCitiesData.countries : [];
    return arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, []);

  // Organize countries for specific layout: Sweden & Denmark in first row, others in second row
  const organizedCountries = useMemo(() => {
    const firstRow = countries.filter(c => 
      c.name?.toLowerCase() === 'sweden' || c.name?.toLowerCase() === 'denmark'
    ).sort((a, b) => {
      // Sweden first, then Denmark
      if (a.name?.toLowerCase() === 'sweden') return -1;
      if (b.name?.toLowerCase() === 'sweden') return 1;
      return 0;
    });
    
    const secondRow = countries.filter(c => 
      c.name?.toLowerCase() !== 'sweden' && c.name?.toLowerCase() !== 'denmark'
    );
    
    return { firstRow, secondRow };
  }, [countries]);

  const renderCountryCard = (country) => (
    <Link
      key={country.code || country.id}
      to={`/country/${slugify(String(country.name || ''))}/cities/${slugify(String(country.capital || ''))}`}
      className="country-card group relative h-64 md:h-80 rounded-2xl overflow-hidden block border border-card-border shadow-sm card-hover"
    >
      <img
        src={country.image}
        alt={`${t(`country.${slugify(String(country.name || ''))}.name`, { defaultValue: country.name })} image`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        style={{ zIndex: 0 }}
      />
      <div
        className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent"
        style={{ zIndex: 1 }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 md:p-8 text-center" style={{ zIndex: 2 }}>
        <h2 className="relative inline-block text-white font-bold text-3xl md:text-4xl lg:text-5xl mb-2 drop-shadow-lg after:absolute after:-bottom-1 after:left-0 after:h-[3px] after:bg-white/90 after:w-full after:transform after:origin-center after:scale-x-50 group-hover:after:scale-x-100 after:transition-transform after:duration-300 after:ease-out">
          {t(`country.${slugify(String(country.name || ''))}.name`, { defaultValue: country.name })}
        </h2>
        <p className="text-white/90 text-lg md:text-xl font-medium drop-shadow">
          {t('countries.exploreCountryCreatives', { 
            country: country.name,
            defaultValue: `Explore ${country.name}'s creatives` 
          })}
        </p>
      </div>
    </Link>
  );

  return (
    <>
      <SEO
        title={t('countries.seoTitle', { defaultValue: 'Countries – Creative Culture' })}
        description={t('countries.seoDescription', { defaultValue: 'Browse countries and explore cities across Scandinavia and the Nordics.' })}
      />
      
      {/* Header section with container */}
      <section className="container mx-auto px-4 py-8">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-primary-text">{t('countries.title', { defaultValue: 'Countries' })}</h1>
          <p className="text-base md:text-lg text-secondary-text">{t('countries.subtitle', { defaultValue: 'Browse countries and discover cities with creative culture.' })}</p>
        </div>
      </section>

      {/* Full-width country cards with safe side margins */}
      <div className="w-full px-4 md:px-6 lg:px-8 space-y-4 overflow-x-hidden">
        {/* First row: Sweden and Denmark */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {organizedCountries.firstRow.map(renderCountryCard)}
        </div>
        
        {/* Second row: Norway, Finland, Iceland */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizedCountries.secondRow.map(renderCountryCard)}
        </div>
      </div>
    </>
  );
}

/**/ 
